import React, { useMemo } from 'react';
import { downloadToExcel } from '../../../utils/ExportUtils';

const CEOReport = ({ data }) => {
  const { companies, contacts, clients, jobs, candidates, tasks, employees, invoices } = data;

  const placements = invoices.filter((inv) => inv.candidateId || inv.candidate_id || inv.candidate).length;
  const totalRevenue = invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
  const paidRevenue = invoices.filter((inv) => inv.status === 'PAID').reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

  const reportMetrics = useMemo(() => {
    return {
      // Core Metrics
      companies: companies.length,
      contacts: contacts.length,
      clients: clients.length,
      jobs: jobs.length,
      candidates: candidates.length,
      placements: placements,
      invoices: invoices.length,
      employees: employees.length,

      // Conversion Metrics
      leadConversion: companies.length > 0 ? ((contacts.length / companies.length) * 100).toFixed(1) : 0,
      clientConversion: contacts.length > 0 ? ((clients.length / contacts.length) * 100).toFixed(1) : 0,
      placementRate: candidates.length > 0 ? ((placements / candidates.length) * 100).toFixed(1) : 0,
      jobsPerClient: clients.length > 0 ? (jobs.length / clients.length).toFixed(1) : 0,
      candidatesPerJob: jobs.length > 0 ? (candidates.length / jobs.length).toFixed(1) : 0,

      // Revenue Metrics
      totalRevenue: (totalRevenue / 100000).toFixed(2),
      paidRevenue: (paidRevenue / 100000).toFixed(2),
      pendingRevenue: ((totalRevenue - paidRevenue) / 100000).toFixed(2),
      revenuePerPlacement: placements > 0 ? (totalRevenue / placements / 100000).toFixed(2) : 0,
      collectionRate: invoices.length > 0 ? ((paidRevenue / totalRevenue) * 100).toFixed(1) : 0,

      // Team Performance
      tasksCompleted: tasks.filter((t) => t.status === 'COMPLETED').length,
      activeEmployees: employees.filter((e) => e.status === 'ACTIVE').length,
    };
  }, [companies, contacts, clients, jobs, candidates, tasks, employees, invoices, placements, totalRevenue, paidRevenue]);

  const handleDownloadReport = () => {
    const reportDate = new Date().toLocaleDateString();
    
    const rows = [
      // Key Business Metrics
      ['KEY BUSINESS METRICS', reportDate],
      [],
      ['Metric', 'Value'],
      ['Companies Identified', reportMetrics.companies],
      ['Leads Generated', reportMetrics.contacts],
      ['Clients Converted', reportMetrics.clients],
      ['Jobs Received', reportMetrics.jobs],
      ['Candidates Submitted', reportMetrics.candidates],
      ['Placements Completed', reportMetrics.placements],
      ['Invoices Raised', reportMetrics.invoices],
      [],
      
      // Conversion Metrics
      ['CONVERSION & SUCCESS RATES', ''],
      [],
      ['Metric', 'Rate'],
      ['Lead Conversion Rate (%)', reportMetrics.leadConversion],
      ['Client Conversion Rate (%)', reportMetrics.clientConversion],
      ['Placement Success Rate (%)', reportMetrics.placementRate],
      ['Jobs per Client', reportMetrics.jobsPerClient],
      ['Candidates per Job', reportMetrics.candidatesPerJob],
      [],
      
      // Financial Metrics
      ['FINANCIAL METRICS', ''],
      [],
      ['Metric', 'Amount (₹L)'],
      ['Total Revenue', reportMetrics.totalRevenue],
      ['Paid Revenue', reportMetrics.paidRevenue],
      ['Pending Revenue', reportMetrics.pendingRevenue],
      ['Revenue per Placement (₹L)', reportMetrics.revenuePerPlacement],
      ['Collection Rate (%)', reportMetrics.collectionRate],
      [],
      
      // Team Metrics
      ['TEAM PERFORMANCE', ''],
      [],
      ['Metric', 'Value'],
      ['Active Employees', reportMetrics.activeEmployees],
      ['Tasks Completed', reportMetrics.tasksCompleted],
      ['Total Employees', reportMetrics.employees],
    ];

    downloadToExcel(rows, ['Business Intelligence Report', ''], `CEO_Executive_Report`);
  };

  return (
    <div className="report-section">
      <div className="report-header">
        <h2>👔 CEO Executive Report</h2>
        <button className="btn-download excel" onClick={handleDownloadReport}>
          📥 Download Full Report
        </button>
      </div>

      {/* Summary Cards */}
      <div className="executive-summary">
        <div className="summary-grid">
          <div className="summary-card metric-card">
            <div className="card-icon">🏢</div>
            <div className="card-content">
              <div className="card-label">Companies</div>
              <div className="card-value">{reportMetrics.companies}</div>
            </div>
          </div>
          <div className="summary-card metric-card">
            <div className="card-icon">👥</div>
            <div className="card-content">
              <div className="card-label">Leads</div>
              <div className="card-value">{reportMetrics.contacts}</div>
            </div>
          </div>
          <div className="summary-card metric-card">
            <div className="card-icon">🤝</div>
            <div className="card-content">
              <div className="card-label">Clients Won</div>
              <div className="card-value">{reportMetrics.clients}</div>
            </div>
          </div>
          <div className="summary-card metric-card">
            <div className="card-icon">✅</div>
            <div className="card-content">
              <div className="card-label">Placements</div>
              <div className="card-value">{reportMetrics.placements}</div>
            </div>
          </div>
          <div className="summary-card metric-card highlight">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <div className="card-label">Total Revenue</div>
              <div className="card-value">₹{reportMetrics.totalRevenue}L</div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Tables */}
      <div className="metrics-section">
        <div className="metrics-row">
          <div className="metrics-box">
            <h3>Conversion Metrics</h3>
            <div className="metrics-table">
              <div className="metric-row">
                <span>Lead Conversion</span>
                <strong>{reportMetrics.leadConversion}%</strong>
              </div>
              <div className="metric-row">
                <span>Client Conversion</span>
                <strong>{reportMetrics.clientConversion}%</strong>
              </div>
              <div className="metric-row">
                <span>Placement Rate</span>
                <strong>{reportMetrics.placementRate}%</strong>
              </div>
              <div className="metric-row">
                <span>Jobs per Client</span>
                <strong>{reportMetrics.jobsPerClient}</strong>
              </div>
            </div>
          </div>

          <div className="metrics-box">
            <h3>Financial Metrics</h3>
            <div className="metrics-table">
              <div className="metric-row">
                <span>Total Revenue</span>
                <strong>₹{reportMetrics.totalRevenue}L</strong>
              </div>
              <div className="metric-row">
                <span>Paid Revenue</span>
                <strong>₹{reportMetrics.paidRevenue}L</strong>
              </div>
              <div className="metric-row">
                <span>Pending Revenue</span>
                <strong>₹{reportMetrics.pendingRevenue}L</strong>
              </div>
              <div className="metric-row">
                <span>Collection Rate</span>
                <strong>{reportMetrics.collectionRate}%</strong>
              </div>
            </div>
          </div>

          <div className="metrics-box">
            <h3>Team Metrics</h3>
            <div className="metrics-table">
              <div className="metric-row">
                <span>Active Employees</span>
                <strong>{reportMetrics.activeEmployees}</strong>
              </div>
              <div className="metric-row">
                <span>Tasks Completed</span>
                <strong>{reportMetrics.tasksCompleted}</strong>
              </div>
              <div className="metric-row">
                <span>Invoices Raised</span>
                <strong>{reportMetrics.invoices}</strong>
              </div>
              <div className="metric-row">
                <span>Avg Revenue/Placement</span>
                <strong>₹{reportMetrics.revenuePerPlacement}L</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Pipeline */}
      <div className="pipeline-summary">
        <h3>Complete Business Pipeline</h3>
        <div className="pipeline-text">
          We contacted <strong>{reportMetrics.companies}</strong> companies → generated <strong>{reportMetrics.contacts}</strong> leads 
          → converted <strong>{reportMetrics.clients}</strong> clients → received <strong>{reportMetrics.jobs}</strong> jobs 
          → submitted <strong>{reportMetrics.candidates}</strong> candidates → completed <strong>{reportMetrics.placements}</strong> placements 
          → raised <strong>{reportMetrics.invoices}</strong> invoices → collected <strong>₹{reportMetrics.paidRevenue}L</strong> revenue.
        </div>
      </div>
    </div>
  );
};

export default CEOReport;
